import React from 'react';
import { useNavigate } from 'react-router';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
} from '@mui/material';
import { Login, Logout } from '@mui/icons-material';
import { useAuthStore } from '@/store';
import { useAdminMode, useMePlayer } from '@/hooks/useManage';
import { resolveMenuSections } from './navigation';

interface MenuDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const MenuDrawer: React.FC<MenuDrawerProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuthStore();
  const { adminMode, isSuperAdmin } = useAdminMode();
  const { data: me } = useMePlayer();
  const player = me?.player;
  const sections = resolveMenuSections({ isAuthenticated, showSports: adminMode && isSuperAdmin });

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  const handleLogout = () => {
    onClose();
    logout();
    navigate('/');
  };

  const playerName = player ? [player.firstName, player.lastName].filter(Boolean).join(' ') : '';

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '85vh',
          pb: 'env(safe-area-inset-bottom)',
          maxWidth: 720,
          mx: 'auto',
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        {isAuthenticated ? (
          <ListItemButton
            onClick={() => go(player?.id ? `/players/${player.id}` : '/profile')}
            sx={{ borderRadius: 2, minHeight: 56 }}
          >
            <Avatar sx={{ mr: 1.5 }}>{playerName.slice(0, 1) || undefined}</Avatar>
            <ListItemText primary="Мой профиль" secondary={playerName || 'Профиль и вход'} />
          </ListItemButton>
        ) : (
          <Button fullWidth size="large" variant="contained" startIcon={<Login />} onClick={() => go('/login')}>
            Войти
          </Button>
        )}
      </Box>
      <Divider />
      {sections.map((section) => (
        <List
          key={section.id}
          dense
          subheader={<ListSubheader sx={{ bgcolor: 'inherit', lineHeight: '36px' }}>{section.title}</ListSubheader>}
        >
          {section.items.map(({ id, path, label, icon: Icon }) => (
            <ListItemButton key={id} onClick={() => go(path)} sx={{ minHeight: 48 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={label} />
            </ListItemButton>
          ))}
        </List>
      ))}
      {isAuthenticated && (
        <>
          <Divider />
          <List>
            <ListItemButton onClick={handleLogout} sx={{ minHeight: 48, color: 'error.main' }}>
              <ListItemIcon sx={{ minWidth: 40, color: 'error.main' }}>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Выйти" />
            </ListItemButton>
          </List>
        </>
      )}
    </Drawer>
  );
};
